import React, { SyntheticEvent, useMemo, useState } from 'react';
import { Box, Stack, Slider, Typography } from '@mui/material';
import { SubtleGrey, useWindowDimensions, MultiSelect, useSnackbar } from '@Iot-Bee/standard-web-library';
import DateSelector from '../../../../components/form/DateSelector';
import dayjs, { Dayjs } from 'dayjs';
import { formatValueLabel } from '../../util';
import { DashboardDeviceData, DashboardFilterData } from '../../interfaces';
import DeviceSelector from './DeviceSelector';
import { SelectDevice, SelectTag } from './types';
import DateRangePicker from './DateRangePicker';
import { TimeRange } from '../../types';

interface DashboardDataFilterProps {
  devicesData: DashboardDeviceData[];
  dashboardFilterData: DashboardFilterData;
  friendlyNameMap: Map<string, string>;
  handleDashboardFilterDataChange: (updatedData: DashboardFilterData) => void;
  handleFromDateChange: (fromDate: Dayjs) => void;
  handleToDateChange: (toDate: Dayjs) => void;
}

const DashboardDataFilter: React.FC<DashboardDataFilterProps> = ({
  devicesData,
  dashboardFilterData,
  friendlyNameMap,
  handleDashboardFilterDataChange,
  handleFromDateChange,
  handleToDateChange,
}) => {
  const {
    actions: { openSnackbar },
  } = useSnackbar();

  // wh = Working hours
  const [whRangeDisplay, setWhRangeDisplay] = useState<TimeRange>(dashboardFilterData.workingHoursRange);

  const tags = useMemo(() => {
    const tagMap = new Map<string, SelectDevice[]>();

    devicesData.forEach((device) => {
      const deviceEntry: SelectDevice = {
        id: device.sensorId,
        name: friendlyNameMap.get(device.sensorId) ?? device.sensorId,
      };
      if (device.tags.length > 0) {
        device.tags.forEach((tag) => {
          if (!tagMap.has(tag)) {
            tagMap.set(tag, []);
          }
          tagMap.get(tag)!.push(deviceEntry);
        });
      } else {
        if (!tagMap.has('Untagged Devices')) {
          tagMap.set('Untagged Devices', []);
        }
        tagMap.get('Untagged Devices')!.push(deviceEntry);
      }
    });

    // Convert the Map into an array of Tag objects
    const tags: SelectTag[] = Array.from(tagMap.entries()).map(([tagName, devices]) => ({
      id: tagName,
      name: tagName,
      devices,
    }));

    return tags;
  }, [devicesData]);

  // this function only updates the text
  const onWorkingHoursChange = (_value: Event | SyntheticEvent<Element, Event>, newRange: number | number[]) => {
    if (!Array.isArray(newRange)) return;
    const range: [number, number] = [newRange[0], newRange[1]];
    setWhRangeDisplay(range);
  };

  // this function updates the text and the filter
  const commitWorkingHoursChange = (_value: Event | SyntheticEvent<Element, Event>, newRange: number | number[]) => {
    if (!Array.isArray(newRange)) return;
    const range: [number, number] = [newRange[0], newRange[1]];
    handleDashboardFilterDataChange({ ...dashboardFilterData, workingHoursRange: range });
    setWhRangeDisplay(range);
  };

  const onFromDateChange = (value: Dayjs | null) => {
    if (value === null) return;
    const newFromDate = value.startOf('day');
    if (newFromDate.isAfter(dayjs(dashboardFilterData.toDate))) {
      openSnackbar('error', 'You set the from date to be after to date!');
    } else {
      handleFromDateChange(newFromDate);
    }
  };

  const onToDateChange = (value: Dayjs | null) => {
    if (value === null) return;
    const newToDate = value.endOf('day');
    if (newToDate.isBefore(dayjs(dashboardFilterData.fromDate))) {
      openSnackbar('error', 'You set the to date to be before from date!');
    } else {
      handleToDateChange(newToDate);
    }
  };

  const { screenwidth } = useWindowDimensions();

  if (screenwidth > 768) {
    return (
      <Stack
        direction="row"
        spacing={10}
        sx={{
          p: '20px',
          background: SubtleGrey,
          alignItems: 'center',
        }}
      >
        <DeviceSelector
          tags={tags}
          selectedDevices={dashboardFilterData.selectedDevices}
          setSelectedDevices={(devices) => {
            handleDashboardFilterDataChange({ ...dashboardFilterData, selectedDevices: devices });
          }}
          friendlyNameMap={friendlyNameMap}
        />

        <DateRangePicker
          fromDate={dashboardFilterData.fromDate}
          handleFromDateChange={onFromDateChange}
          toDate={dashboardFilterData.toDate}
          handleToDateChange={onToDateChange}
        />

        <Box
          sx={{
            width: '25%',
          }}
        >
          <Typography id="input-slider" gutterBottom>
            Working Hours: {formatValueLabel(whRangeDisplay[0])} - {formatValueLabel(whRangeDisplay[1])}
          </Typography>
          <Slider
            sx={{
              width: '100%',
            }}
            getAriaLabel={() => 'Temperature range'}
            value={whRangeDisplay}
            onChange={onWorkingHoursChange}
            onChangeCommitted={commitWorkingHoursChange}
            min={0}
            step={30}
            max={1439}
            valueLabelDisplay="auto"
            valueLabelFormat={formatValueLabel}
          />
        </Box>
      </Stack>
    );
  } else {
    return (
      <Box
        sx={{
          p: '10px',
          background: 'SubtleGrey',
          display: 'flex',
          flexFlow: 'row wrap',
          justifyContent: 'center',
          gap: '10px',
          alignItems: 'center',
        }}
      >
        <MultiSelect
          options={devicesData.map((device) => device.sensorId)}
          value={dashboardFilterData.selectedDevices}
          onChange={() => {}}
          label="Devices"
          size="small"
          sx={{
            width: '330px',
            marginBottom: '15px',
          }}
        />
        <MultiSelect
          options={dashboardFilterData.availableTags}
          value={dashboardFilterData.selectedTags}
          onChange={() => {}}
          label="Tags"
          size="small"
          sx={{
            width: '330px',
            marginBottom: '15px',
          }}
        />
        <Box>
          <DateSelector label="From date" value={dashboardFilterData.fromDate} onChange={handleFromDateChange} />
          <DateSelector label="To date" value={dashboardFilterData.toDate} onChange={handleToDateChange} />
        </Box>
        <Box>
          <Typography id="input-slider" gutterBottom>
            Working Hours
          </Typography>
          <Slider
            sx={{
              width: '330px',
            }}
            getAriaLabel={() => 'Temperature range'}
            value={dashboardFilterData.workingHoursRange}
            onChange={onWorkingHoursChange}
            min={0}
            step={30}
            max={1439}
            valueLabelDisplay="auto"
            valueLabelFormat={formatValueLabel}
          />
        </Box>
      </Box>
    );
  }
};

export default DashboardDataFilter;
